import { nsDfs } from '@qdabra/forms-shared/xmlNamespaces';
import { isAttachment } from '@qdabra/infopath-attachments';
import FVNamespaceResolver from '../../../../fvNamespaceResolver';
import { makeXPathEngine } from '../../../utility/xmlHelpers';
import { getNodeValue, xmlToString } from '../../../xmlUtility';
import {
    paramAutoOpen,
    paramDomainId,
    paramDsName,
    paramQdImage,
    paramSubmit,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import {
    clearFieldAndSetLinkAsync,
    getBase64ValuesForNode,
    verifyRequiredAttributesExist,
} from '../helpers/attachmentHelpers';
import { commandResult } from '../helpers/commandResult';
import {
    NS_DATABASE_WEBSERVICE,
    NS_QDABRA_WEBSERVICE,
} from '../helpers/dbxl.helpers';
import {
    getDataSource,
    verifySecondaryDataSource,
} from '../helpers/qRules.dataSource.helpers';
import {
    getValidNodeSet,
    setNodeListValueFromXPathEngine,
} from '../helpers/qRules.node.helpers';
import {
    selectNodeFromXPathEngine,
    setNodeValueFromXPathEngine,
} from '../helpers/qRules.utility';
import submitDocumentWrapper from '../helpers/submitDocumentWrapper';

const $ = window.jQuery;

const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data Source Name',
        type: typeDataSource,
    },
    {
        name: paramQdImage,
        description:
            '(boolean, defaults to false) Create a QdImage Document for a Saved Picture (yes | no)]',
        type: typeBoolean,
    },
    {
        name: paramAutoOpen,
        description:
            '(boolean, defaults to false) Use links that automatically open the saved-out files rather than the documents they are stored in.',
        type: typeBoolean,
    },
    {
        name: paramDomainId,
        description: 'A DBXL domain with which to associate the saved-out file',
    },
];
const requiredParameters = [
    {
        name: paramSubmit,
        description: 'DBXL Submit Adapter Name',
        type: typeDataSource,
    },
    {
        name: paramXPath,
        description: 'XPath to Picture(s) or Attachment(s)',
        type: typeXPath,
        dsParentName: paramDsName,
    },
];
const saveToDbxlQdFileXmlTemplate =
    "<?xml version='1.0' encoding='UTF-8'?><?mso-infoPathSolution " +
    "name='urn:schemas-microsoft-com:office:infopath:QdFile:-myXSD-2008-08-21T22-28-06' href='manifest.xsf' solutionVersion='1.0.0.3' " +
    "productVersion='12.0.0' PIVersion='1.0.0.0' ?><?mso-application progid='InfoPath.Document'?><?mso-infoPath-file-attachment-present?>" +
    "<my:QdFile xmlns:my='http://schemas.microsoft.com/office/infopath/2003/myXSD/2008-08-21T22:28:06' xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance'>" +
    "<my:File/><my:DomainId xsi:nil='true'/></my:QdFile>";
const saveToDbxlQdImageXmlTemplate =
    "<?xml version='1.0' encoding='UTF-8'?><?mso-infoPathSolution " +
    "name='urn:schemas-microsoft-com:office:infopath:QdImage:-myXSD-2009-01-23T19-16-16' href='manifest.xsf' solutionVersion='1.0.0.1' " +
    "productVersion='12.0.0' PIVersion='1.0.0.0'?><?mso-application progid='InfoPath.Document' versionProgid='InfoPath.Document.2'?>" +
    "<my:QdImage xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance' xmlns:my='http://schemas.microsoft.com/office/infopath/2003/myXSD/2009-01-23T19:16:16'>" +
    "<my:DatePublished/><my:UserName/><my:Image my:qRulesLink='' my:qRulesFilename='' my:qdFileDocId='' my:qdDomainId='' xsi:nil='true'/>" +
    '<my:DateTaken/><my:Title/><my:Subject/><my:Location/><my:Creator/><my:Copyright/><my:IsPrivate>false</my:IsPrivate><my:Tags/></my:QdImage>';
const saveToDbxlQdAttachmentXmlTemplate =
    "<?xml version='1.0' encoding='UTF-8'?><?mso-infoPathSolution " +
    "name='urn:schemas-microsoft-com:office:infopath:QdAttachment:-myXSD-2009-01-23T19-16-16' href='manifest.xsf' solutionVersion='1.0.0.1' " +
    "productVersion='12.0.0' PIVersion='1.0.0.0'?><?mso-application progid='InfoPath.Document' versionProgid='InfoPath.Document.2'?>" +
    "<?mso-infoPath-file-attachment-present ?><my:QdAttachment xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance' " +
    "xmlns:my='http://schemas.microsoft.com/office/infopath/2003/myXSD/2009-01-23T19:16:16'><my:DatePublished/><my:UserName/>" +
    "<my:File my:qRulesLink='' my:qRulesFilename='' my:qdFileDocId='' my:qdDomainId='' xsi:nil='true'/><my:DateCreated/><my:Title/><my:Subject/>" +
    '<my:Description/><my:Creator/><my:Copyright/><my:IsPrivate>false</my:IsPrivate><my:Tags/></my:QdAttachment>';
const saveToDbxlQdFileDocTypeName = 'QdFile';
const saveToDbxlQdImageDocTypeName = 'QdImage';
const saveToDbxlQdAttachmentDocTypeName = 'QdAttachment';
const dbxlQdflowXmlTemplate =
    "<?xml version='1.0' encoding='UTF-8'?><?mso-infoPathSolution name='urn:schemas-microsoft-com:office:infopath:EmailFlow:-myXSD-2006-12-05T15-51-37' ?>" +
    "<?mso-application progid='InfoPath.Document'?>" +
    "<my:myFields xmlns:my='http://schemas.microsoft.com/office/infopath/2003/myXSD/2006-12-05T15:51:37'>" +
    '<my:HostDocType />' +
    '<my:HostDocId />' +
    '<my:Alias />' +
    '<my:Name />' +
    '<my:Comment />' +
    '<my:Date />' +
    '<my:Time />' +
    '<my:Status />' +
    '<my:AssignedToAlias />' +
    '<my:AssignedToName />' +
    '<my:CCAddresses />' +
    '</my:myFields>';
const dbxlQdflowXmlTemplateWithEmail =
    "<?xml version='1.0' encoding='UTF-8'?><?mso-infoPathSolution name='urn:schemas-microsoft-com:office:infopath:EmailFlow:-myXSD-2006-12-05T15-51-37' ?><?mso-application progid='InfoPath.Document'?>" +
    "<my:myFields xmlns:my='http://schemas.microsoft.com/office/infopath/2003/myXSD/2006-12-05T15:51:37'>" +
    '<my:HostDocType />' +
    '<my:HostDocId />' +
    '<my:Alias />' +
    '<my:Name />' +
    '<my:Comment />' +
    '<my:Date />' +
    '<my:Time />' +
    '<my:Status />' +
    '<my:AssignedToAlias />' +
    '<my:AssignedToName />' +
    '<my:AssignedToEmail />' +
    '<my:CCAddresses />' +
    '</my:myFields>';
const dbxlQdflowMyNamespace =
    'http://schemas.microsoft.com/office/infopath/2003/myXSD/2006-12-05T15:51:37';
const dbxlQdflowDoctype = 'QdEmailFlow';
const saveToDbxlErrorQdImage =
    'In order to create a QdImage document type along with the QdFile document type, the file must be attached using a picture control, not a file attachment control, and /qdimage must be set to yes.';
const description =
    'Saves a file, a set of files, an image or a set of images to a DBXL DocType called QdFile (please refer to the qRules User Guide for details).';

function createXPathEngine(nameSpaceList) {
    let nsr = new FVNamespaceResolver();

    if (nameSpaceList) {
        nameSpaceList.forEach(function (nameSpace) {
            nsr.addNamespace(nameSpace.name, nameSpace.url);
        });
    }

    return makeXPathEngine(nsr);
}

function SaveToDbxl(params) {
    const utilities = params.utilities;

    function submitQdFileToDbxl(cf, submit, domainId, node) {
        var properties = getBase64ValuesForNode(node),
            fileName = properties.fileName,
            // 2023-10-27 removed unused access to properties.fileExtension
            fileSize = properties.fileSize,
            base64 = properties.base64,
            xdoc = $.parseXML(saveToDbxlQdFileXmlTemplate),
            xPathEngine = params.xpathEngine;

        setNodeValueFromXPathEngine(xPathEngine, '/*/*[1]', xdoc, base64);

        if (domainId) {
            setNodeValueFromXPathEngine(xPathEngine, '/*/*[2]', xdoc, domainId);
        }

        const submitConnection = params.dataConnections.get(submit);
        const submitSource = cf.getDataSource(submit);
        const submitDocumentObj = submitDocumentWrapper.standard(
            submitSource,
            submitConnection,
        );
        const userName = ''; //TODO:Get Username;

        return submitDocumentObj
            .querySubmitDocument(
                saveToDbxlQdFileDocTypeName,
                xmlToString(xdoc),
                fileName,
                userName,
                fileSize + ' bytes',
            )
            .then(function (result) {
                return {
                    base64,
                    docId: result.docId,
                    fileName,
                };
            });
    }

    function getDbxlDocumentsPath(submit) {
        var conn = params.dataConnections.get(submit),
            docUrl = conn.getUrl(),
            i = docUrl.toLowerCase().lastIndexOf('/');

        docUrl = i > 0 ? docUrl.substr(0, i) + '/Documents' : null;

        return docUrl;
    }

    function getUserInfo(dsRoot, dataConn) {
        const dataSource = dsRoot;
        const dataConnection = dataConn;
        const xPathEngine = createXPathEngine([
            {
                name: 'dfs',
                url: nsDfs,
            },
            {
                name: 'tns',
                url: NS_DATABASE_WEBSERVICE,
            },
            {
                name: 'ns2',
                url: NS_QDABRA_WEBSERVICE,
            },
        ]);
        let querySucceded;
        // BUGBUG - Assigned but never used
        let exception;

        function performQueryAsync() {
            return dataConnection
                .executeAsync()
                .then(function () {
                    querySucceded = true;
                    return true;
                })
                .catch(function (e) {
                    querySucceded = false;
                    exception = e;
                    return false;
                });
        }

        function getPropertyValue(key) {
            if (!querySucceded) {
                exception = new Error(
                    'No query has been successfully performed.  The data is not available',
                );
                return null;
            }

            var infoFields = selectNodeFromXPathEngine(
                xPathEngine,
                'dfs:myFields/dfs:dataFields/node()/node()',
                dataSource.getNode(),
            );
            if (!infoFields || infoFields.length === 0) {
                exception = new Error(
                    'No data was found for the specified user.',
                );
                return null;
            }

            var propPath = String.format(
                'ns2:ADProp[ns2:Key = "{0}"]/ns2:Value',
                key,
            ); //TODO:Use SecurityElement.Escape(key) instead of key ??
            infoFields = selectNodeFromXPathEngine(
                xPathEngine,
                propPath,
                infoFields,
            );

            return infoFields ? getNodeValue(infoFields) : null;
        }

        return {
            performQueryAsync,
            // BUGBUG - returning this here seems to make no sense
            //          since no meaningful value has been assigned to it
            //          when this return happens and it is not mutable
            querySucceded,
            // BUGBUG: 2023-10-27 Not used anywhere, but I am adding this as an alternative
            //         to removing the code that assigns values to exception
            getException: () => exception,
            getPropertyValue,
        };
    }

    function getUserIdentityAsync(userInfo) {
        if (!userInfo) {
            var userName = ''; //TODO:Get UserName
            return Promise.resolve({
                modifiedByAlias: userName,
                modifiedByName: userName,
            });
        }

        var myInfoConnection = params.dataConnections.get(userInfo),
            myInfoSource = params.commonFunction.getDataSource(userInfo),
            userInfoObj = getUserInfo(myInfoSource, myInfoConnection),
            modifiedByAlias,
            modifiedByName;

        return userInfoObj.performQueryAsync().then(function () {
            modifiedByAlias = userInfoObj.getPropertyValue('sAMAccountName');
            if (userInfoObj.querySucceded && modifiedByAlias) {
                modifiedByName = userInfoObj.getPropertyValue('cn');

                if (!modifiedByName) {
                    modifiedByName = modifiedByAlias;
                }
            } else {
                modifiedByAlias = userName;
                modifiedByName = userName;
            }

            return {
                modifiedByAlias: modifiedByAlias,
                modifiedByName: modifiedByName,
            };
        });
    }

    function doAssignDocumentAsync(
        submit,
        docId,
        comment,
        status,
        assignedToAlias,
        assignedToName,
        assignedToEmail,
        cc,
        ccEmail,
        myInfo,
    ) {
        return getUserIdentityAsync(myInfo).then(function (userInfo) {
            var cf = params.commonFunction,
                modifiedByAlias = userInfo.modifiedByAlias,
                modifiedByName = userInfo.modifiedByName,
                milliSeconds = new Date().getTime().toString(),
                date = milliSeconds.substr(0, 10),
                time = milliSeconds.substr(11, 8),
                flowXml = assignedToEmail
                    ? dbxlQdflowXmlTemplateWithEmail
                    : dbxlQdflowXmlTemplate,
                xDocEmailFlow = $.parseXML(flowXml),
                xPathEngine = createXPathEngine([
                    {
                        name: 'my',
                        url: dbxlQdflowMyNamespace,
                    },
                ]),
                rootNav = selectNodeFromXPathEngine(
                    xPathEngine,
                    '/my:myFields',
                    xDocEmailFlow,
                );

            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:HostDocId',
                rootNav,
                docId,
            );
            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:Alias',
                rootNav,
                modifiedByAlias,
            );
            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:Name',
                rootNav,
                modifiedByName,
            );
            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:Comment',
                rootNav,
                comment,
            );
            setNodeValueFromXPathEngine(xPathEngine, 'my:Date', rootNav, date);
            setNodeValueFromXPathEngine(xPathEngine, 'my:Time', rootNav, time);
            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:Status',
                rootNav,
                status,
            );
            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:AssignedToAlias',
                rootNav,
                assignedToAlias,
            );
            setNodeValueFromXPathEngine(
                xPathEngine,
                'my:AssignedToName',
                rootNav,
                assignedToName,
            );

            if (assignedToEmail) {
                setNodeValueFromXPathEngine(
                    xPathEngine,
                    'my:AssignedToEmail',
                    rootNav,
                    assignedToEmail,
                );
            }

            if (cc) {
                //TODO:Implement
            }

            var submitConnection = params.dataConnections.get(submit),
                submitSource = cf.getDataSource(submit),
                // BUGBUG: This .submitDocument() method appears to not exist
                submitDocumentObj = utilities.submitDocument(
                    submitSource.getNode(),
                    submitConnection,
                );

            return submitDocumentObj
                .querySubmitDocument(
                    dbxlQdflowDoctype,
                    xmlToString(xDocEmailFlow),
                    'Document Assignment',
                    modifiedByAlias,
                    comment,
                )
                .then(function (result) {
                    //TODO:Set Result here ?

                    return !!result.docId;
                });
        });
    }

    function submitQdImageToDbXl(
        submit,
        link,
        fileName,
        qdFileDocId,
        domainId,
    ) {
        var cf = params.commonFunction,
            user = '', //TODO:Get UserName
            dateTime = new Date().toISOString(),
            title = '[New Untitled - ' + user + ']',
            xPathEngine = createXPathEngine([
                {
                    name: 'my',
                    url: 'http://schemas.microsoft.com/office/infopath/2003/myXSD/2009-01-23T19:16:16',
                },
            ]),
            doc = $.parseXML(saveToDbxlQdImageXmlTemplate);

        var qdImageNode = selectNodeFromXPathEngine(
            xPathEngine,
            '/my:QdImage',
            doc,
        );

        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:DatePublished',
            dateTime,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:UserName',
            user,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:Image/@my:qRulesLink',
            link,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:Image/@my:qRulesFilename',
            fileName,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:Image/@my:qdFileDocId',
            qdFileDocId,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:Image/@my:qdDomainId',
            domainId,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:DateTaken',
            dateTime.substr(0, 10),
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdImageNode,
            'my:Title',
            title,
        );

        var submitConnection = params.dataConnections.get(submit),
            submitSource = cf.getDataSource(submit),
            // BUGBUG: This .submitDocument() method appears to not exist
            submitDocumentObj = utilities.submitDocument(
                submitSource.getNode(),
                submitConnection,
            );

        return submitDocumentObj
            .querySubmitDocument(
                saveToDbxlQdImageDocTypeName,
                xmlToString(doc),
                title,
                user,
                '',
            )
            .then(function (data) {
                var comment = 'New Image Submitted',
                    status = 'Submitted';

                return doAssignDocumentAsync(
                    submit,
                    data.docId,
                    comment,
                    status,
                    user,
                    user,
                    '',
                    '',
                    '',
                    null,
                );
            });
    }

    function submitQdAttachmentToDbxl(
        submit,
        link,
        fileName,
        qdFileDocId,
        domainId,
    ) {
        var cf = params.commonFunction,
            user = '', //TODO:Get UserName
            dateTime = new Date().toISOString().toString(),
            xPathEngine = createXPathEngine([
                {
                    name: 'my',
                    url: 'http://schemas.microsoft.com/office/infopath/2003/myXSD/2009-01-23T19:16:16',
                },
            ]),
            doc = $.parseXML(saveToDbxlQdAttachmentXmlTemplate);

        var qdAttachmentNode = selectNodeFromXPathEngine(
            xPathEngine,
            '/my:QdAttachment',
            doc,
        );

        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:DatePublished',
            dateTime,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:UserName',
            user,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:File/@my:qRulesLink',
            link,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:File/@my:qRulesFilename',
            fileName,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:File/@my:qdFileDocId',
            qdFileDocId,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:File/@my:qdDomainId',
            domainId,
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:DateCreated',
            dateTime.substr(0, 10),
        );
        setNodeListValueFromXPathEngine(
            xPathEngine,
            qdAttachmentNode,
            'my:Title',
            fileName,
        );

        var submitConnection = params.dataConnections.get(submit),
            submitSource = cf.getDataSource(submit),
            // BUGBUG: This .submitDocument() method appears to not exist
            submitDocumentObj = utilities.submitDocument(
                submitSource.getNode(),
                submitConnection,
            );

        return submitDocumentObj
            .querySubmitDocument(
                saveToDbxlQdAttachmentDocTypeName,
                xmlToString(doc),
                fileName,
                user,
                '',
            )
            .then(function (data) {
                var comment = 'New Attachment Submitted',
                    status = 'Submitted';

                return doAssignDocumentAsync(
                    submit,
                    data.docId,
                    comment,
                    status,
                    user,
                    user,
                    '',
                    '',
                    '',
                    null,
                );
            });
    }

    function submitMetaDocumentAsync(
        submit,
        qdimage,
        domainId,
        base64Value,
        autoOpenUrl,
        fileName,
        docId,
    ) {
        const valueIsAttachment = isAttachment(base64Value);

        return Promise.resolve()
            .then(function () {
                //TODO:Fix issue with qdImage and isAttachment
                if (qdimage) {
                    if (valueIsAttachment) {
                        return submitQdImageToDbXl(
                            submit,
                            autoOpenUrl,
                            fileName,
                            docId,
                            domainId,
                        );
                    }

                    return true;
                }
                if (valueIsAttachment) {
                    return submitQdAttachmentToDbxl(
                        submit,
                        autoOpenUrl,
                        fileName,
                        docId,
                        domainId,
                    );
                }

                return false;
            })
            .catch(function (e) {
                console.error(e);
                //TODO: Remove ??
                //Handle exception in case QdImage and QdAttachment fails.
                return true;
            });
    }

    async function saveOutFile(cf, submit, qdImage, domainId, node, autoOpen) {
        verifyRequiredAttributesExist(node);

        const domSubmit = cf.getDataSource(submit);
        const base64Value = node.value();

        const data = await submitQdFileToDbxl(cf, submit, domainId, node);

        const docId = data.docId;
        const fileName = data.fileName;

        if (docId) {
            const qdFileUrl = `${getDbxlDocumentsPath(
                submit,
            )}/${docId}/${docId}.xml`;
            const autoOpenUrl = `${qdFileUrl}?cmd=file`;
            const url = autoOpen ? autoOpenUrl : qdFileUrl;

            await clearFieldAndSetLinkAsync(
                node,
                fileName,
                url,
                true /* clear */,
            );

            const result = await submitMetaDocumentAsync(
                submit,
                qdImage,
                domainId,
                base64Value,
                autoOpenUrl,
                fileName,
                docId,
            );

            return {
                Saved: true,
                Error: result,
            };
        }

        const errorDescription = domSubmit.selectSingle(
            '/dfs:myFields/dfs:dataFields/tns:SubmitDocumentResponse/tns:SubmitDocumentResult/tns:Errors/tns:ErrorInfo/tns:Description',
        );

        throw new Error(
            errorDescription
                ? errorDescription.value()
                : 'Unable to save out the file.',
        );
    }

    async function saveOutFiles(
        cf,
        dataSources,
        dsName,
        xPath,
        submit,
        qdImage,
        domainId,
        autoOpen,
    ) {
        const nodes = getValidNodeSet(
            getDataSource(dataSources, dsName),
            xPath,
        ).filter((node) => node.value());
        let resultData = [];

        for (const node of nodes) {
            const result = await saveOutFile(
                cf,
                submit,
                qdImage,
                domainId,
                node,
                autoOpen,
            );

            resultData.push(result);
        }

        const qdImageErrorCount = resultData.filter(
            (result) => result.Error,
        ).length;
        const count = resultData.filter((result) => result.Saved).length;

        return commandResult(
            count > 0,
            count,
            qdImageErrorCount ? saveToDbxlErrorQdImage : '',
        );
    }

    function executeAsync({ dataSources }) {
        var cf = params.commonFunctions,
            submit = cf.getParamValue(paramSubmit),
            dsName = cf.getParamValue(paramDsName),
            xPath = cf.getParamValue(paramXPath),
            qdImage = cf.getBoolParamValue(paramQdImage),
            domainId = cf.getParamValue(paramDomainId, ''),
            autoOpen = cf.getBoolParamValue(paramAutoOpen);

        verifySecondaryDataSource(dataSources, dsName);

        return saveOutFiles(
            cf,
            dataSources,
            dsName,
            xPath,
            submit,
            qdImage,
            domainId,
            autoOpen,
        );
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default SaveToDbxl;
